import { AnimateOnScroll } from "@/components/AnimateOnScroll";
import { Check, X, Minus, LucideIcon } from "lucide-react";
import { cn } from "@/lib/utils";

interface ComparisonFeature {
  feature: string;
  aws: "full" | "partial" | "none";
  azure: "full" | "partial" | "none";
  gcp: "full" | "partial" | "none";
}

interface CloudComparisonSectionProps {
  badge: {
    icon: LucideIcon;
    text: string;
    color: string;
  };
  title: string;
  subtitle?: string;
  highlightProvider: "aws" | "azure" | "gcp";
  features: ComparisonFeature[];
}

const providerColors = {
  aws: {
    bg: "bg-gradient-to-br from-orange-500 to-amber-500",
    text: "text-orange-500",
    border: "border-orange-500/30",
    highlight: "ring-2 ring-orange-500/50 bg-orange-500/5",
  },
  azure: {
    bg: "bg-gradient-to-br from-cyan-500 to-blue-500",
    text: "text-cyan-500",
    border: "border-cyan-500/30",
    highlight: "ring-2 ring-cyan-500/50 bg-cyan-500/5",
  },
  gcp: {
    bg: "bg-gradient-to-br from-blue-500 to-green-500",
    text: "text-blue-500",
    border: "border-blue-500/30",
    highlight: "ring-2 ring-blue-500/50 bg-blue-500/5",
  },
};

const badgeColors: Record<string, string> = {
  orange: "bg-orange-500/10 text-orange-500 border-orange-500/20",
  cyan: "bg-cyan-500/10 text-cyan-500 border-cyan-500/20",
  blue: "bg-blue-500/10 text-blue-500 border-blue-500/20",
  green: "bg-green-500/10 text-green-500 border-green-500/20",
};

const StatusIcon = ({ status, color }: { status: "full" | "partial" | "none"; color: string }) => {
  if (status === "full") {
    return (
      <div className={cn("w-6 h-6 rounded-full flex items-center justify-center", `bg-${color}-500/20`)}>
        <Check className={cn("w-4 h-4", `text-${color}-500`)} />
      </div>
    );
  }
  if (status === "partial") {
    return (
      <div className="w-6 h-6 rounded-full bg-yellow-500/20 flex items-center justify-center">
        <Minus className="w-4 h-4 text-yellow-500" />
      </div>
    );
  }
  return (
    <div className="w-6 h-6 rounded-full bg-muted flex items-center justify-center">
      <X className="w-4 h-4 text-muted-foreground" />
    </div>
  );
};

export const CloudComparisonSection = ({
  badge,
  title,
  subtitle,
  highlightProvider,
  features,
}: CloudComparisonSectionProps) => {
  const BadgeIcon = badge.icon;
  const badgeColor = badgeColors[badge.color] || badgeColors.blue;

  return (
    <section className="py-20 bg-muted/30">
      <div className="container mx-auto px-4 sm:px-6">
        <AnimateOnScroll animation="fade-up">
          <div className="text-center mb-12">
            <div className={cn("inline-flex items-center gap-2 px-4 py-2 rounded-full border mb-4", badgeColor)}>
              <BadgeIcon className="w-4 h-4" />
              <span className="text-sm font-medium">{badge.text}</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-foreground mb-4">{title}</h2>
            {subtitle && <p className="text-muted-foreground max-w-2xl mx-auto">{subtitle}</p>}
          </div>
        </AnimateOnScroll>

        <AnimateOnScroll animation="fade-up" delay={100}>
          <div className="max-w-5xl mx-auto overflow-x-auto">
            <div className="min-w-[600px]">
              {/* Header */}
              <div className="grid grid-cols-4 gap-4 mb-4">
                <div className="p-4">
                  <span className="text-sm font-medium text-muted-foreground">Features</span>
                </div>
                {(["aws", "azure", "gcp"] as const).map((provider) => (
                  <div
                    key={provider}
                    className={cn(
                      "p-4 rounded-xl text-center transition-all",
                      highlightProvider === provider ? providerColors[provider].highlight : "bg-card"
                    )}
                  >
                    <div
                      className={cn(
                        "w-12 h-12 rounded-xl mx-auto mb-2 flex items-center justify-center",
                        providerColors[provider].bg
                      )}
                    >
                      <span className="text-white font-bold text-sm">
                        {provider === "aws" ? "AWS" : provider === "azure" ? "Azure" : "GCP"}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-foreground">
                      {provider === "aws"
                        ? "Amazon AWS"
                        : provider === "azure"
                        ? "Microsoft Azure"
                        : "Google Cloud"}
                    </span>
                    {highlightProvider === provider && (
                      <div className={cn("text-xs mt-1 font-medium", providerColors[provider].text)}>
                        Current Page
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Features */}
              <div className="space-y-2">
                {features.map((item, index) => (
                  <AnimateOnScroll key={index} animation="fade-up" delay={150 + index * 50}>
                    <div className="grid grid-cols-4 gap-4 bg-card rounded-xl p-4 border border-border/50 hover:border-border transition-colors">
                      <div className="flex items-center">
                        <span className="text-sm font-medium text-foreground">{item.feature}</span>
                      </div>
                      <div
                        className={cn(
                          "flex items-center justify-center rounded-lg py-2",
                          highlightProvider === "aws" && "bg-orange-500/5"
                        )}
                      >
                        <StatusIcon status={item.aws} color="orange" />
                      </div>
                      <div
                        className={cn(
                          "flex items-center justify-center rounded-lg py-2",
                          highlightProvider === "azure" && "bg-cyan-500/5"
                        )}
                      >
                        <StatusIcon status={item.azure} color="cyan" />
                      </div>
                      <div
                        className={cn(
                          "flex items-center justify-center rounded-lg py-2",
                          highlightProvider === "gcp" && "bg-blue-500/5"
                        )}
                      >
                        <StatusIcon status={item.gcp} color="green" />
                      </div>
                    </div>
                  </AnimateOnScroll>
                ))}
              </div>

              {/* Legend */}
              <div className="flex items-center justify-center gap-6 mt-8 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-green-500/20 flex items-center justify-center">
                    <Check className="w-3 h-3 text-green-500" />
                  </div>
                  <span>Full Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-yellow-500/20 flex items-center justify-center">
                    <Minus className="w-3 h-3 text-yellow-500" />
                  </div>
                  <span>Partial Support</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-5 h-5 rounded-full bg-muted flex items-center justify-center">
                    <X className="w-3 h-3 text-muted-foreground" />
                  </div>
                  <span>Limited/None</span>
                </div>
              </div>
            </div>
          </div>
        </AnimateOnScroll>
      </div>
    </section>
  );
};
